/** Track-wide helpers of the subtitle editors (1.9.0): the import file types and names, and every cue's live counters. */
import { DEFAULT_SUBTITLE_RULES, cueMetrics, type Cue, type CueMetrics, type TrackStyle } from '@prism/contracts';

/** The subtitle file types the tab imports. */
export const SUBTITLE_IMPORT_ACCEPT = '.srt,.vtt,.ttml,.dfxp,.xml,text/vtt,application/ttml+xml';
const IMPORT_EXT = /\.(srt|vtt|ttml|dfxp|xml)$/i;

/** A file name's language ("interview.fi.srt" → fi) and label ("interview"). */
export function importNameParts(filename: string): { label: string; language: string | null } {
  const base = filename.replace(IMPORT_EXT, '');
  const m = /^(.*)\.([a-z]{2,3}(?:-[A-Za-z0-9]{2,8})?)$/.exec(base);
  if (m && m[1]) return { label: m[1], language: m[2]!.toLowerCase() };
  return { label: base || filename, language: null };
}

/**
 * Every cue's live counters and issues: contracts cueMetrics with the cues it may overlap (a sweep by start, so a long
 * track stays quick).
 */
export function trackMetrics(cues: Cue[], trackStyle: TrackStyle, durationTicks: number | undefined): Map<string, CueMetrics> {
  const sorted = [...cues].sort((a, b) => a.startTicks - b.startTicks);
  const out = new Map<string, CueMetrics>();
  let maxEnd = -1, maxEndAt = -1;
  sorted.forEach((c, i) => {
    const near: Cue[] = [];
    for (let j = i + 1; j < sorted.length && sorted[j]!.startTicks < c.endTicks; j++) near.push(sorted[j]!);
    for (let j = i - 1; j >= 0 && j >= i - 64; j--) if (sorted[j]!.endTicks > c.startTicks) near.push(sorted[j]!);
    if (maxEndAt >= 0 && maxEndAt < i - 64 && maxEnd > c.startTicks) near.push(sorted[maxEndAt]!);
    if (c.endTicks > maxEnd) { maxEnd = c.endTicks; maxEndAt = i; }
    out.set(c.id, cueMetrics(c, DEFAULT_SUBTITLE_RULES, { cues: near, trackStyle, durationTicks }));
  });
  return out;
}

